/**
 * PDP disclosure flyouts — shared side panel with Product Details / Care / FAQ / Élite tabs.
 */
(function () {
  const SOURCE = 'product-disclosure-flyout';

  /**
   * @param {Element} el
   * @returns {HTMLDialogElement | null}
   */
  function getDialog(el) {
    const controlsId =
      el.getAttribute('aria-controls') || el.closest('[aria-controls]')?.getAttribute('aria-controls');
    if (controlsId) {
      const byId = document.getElementById(controlsId);
      if (byId instanceof HTMLDialogElement) return byId;
    }

    const fromClose = el.closest('[data-disclosure-dialog]');
    return fromClose instanceof HTMLDialogElement ? fromClose : null;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {string} tab
   */
  function activateTab(dialog, tab) {
    const tabs = dialog.querySelectorAll('[data-disclosure-tab-btn]');
    const panels = dialog.querySelectorAll('[data-disclosure-panel]');
    let matched = false;

    tabs.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const isActive = btn.getAttribute('data-disclosure-tab-btn') === tab;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;
      if (isActive) matched = true;
    });

    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.getAttribute('data-disclosure-panel') === tab;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.hidden = false;
        panel.removeAttribute('hidden');
      } else {
        panel.hidden = true;
        panel.setAttribute('hidden', '');
      }
    });

    if (!matched) {
      const first = dialog.querySelector('[data-disclosure-tab-btn]');
      if (first instanceof HTMLElement) {
        activateTab(dialog, first.getAttribute('data-disclosure-tab-btn') || '');
      }
    }
  }

  /**
   * @param {HTMLElement} openBtn
   * @param {boolean} expanded
   */
  function setExpanded(openBtn, expanded) {
    openBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Adds the Élite membership to the cart directly instead of sending the shopper to
   * the membership PDP. Falls back to the membership PDP link if the add fails.
   * @param {HTMLButtonElement} button
   */
  async function addEliteMembershipToCart(button) {
    if (button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    const fallbackUrl = button.getAttribute('data-fallback-url');

    if (!variantId) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Ask for the cart sections in the add request, exactly as product-form-component
      // does. With them, cart-items-component morphs its existing markup; without them
      // it re-renders the section from scratch, which replaces the nodes the drawer just
      // put focus on.
      const sectionIds = cartItemsSectionIds();
      if (sectionIds) formData.append('sections', sectionIds);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data?.status) {
        if (fallbackUrl) window.location.href = fallbackUrl;
        return;
      }

      // Announce the add the way every other add-to-cart surface does, so the cart
      // icon, drawer and totals refresh from the sections above before anything opens.
      // cart-app-sync.js sees this event and suppresses the duplicate it would
      // otherwise dispatch for the same request.
      announceCartAdd(data);

      endPendingState(button);

      // Close this modal before opening the drawer: two open <dialog> modals stack,
      // and the flyout would sit behind the drawer holding the focus trap.
      const dialog = button.closest('[data-disclosure-dialog]');
      if (dialog instanceof HTMLDialogElement) dialog.close();

      // The store's automatic drawer-open setting is off, so nothing opens the
      // drawer on add except an explicit call — same as `data-open-cart-drawer-on-add`
      // does for the PDP buy buttons and the PLP quick add, which is why this
      // surface carries that attribute too.
      if (button.dataset.openCartDrawerOnAdd === 'true') {
        const drawer = /** @type {{ open?: () => void } | null} */ (
          document.querySelector('cart-drawer-component')
        );
        if (typeof drawer?.open === 'function') drawer.open();
      }
    } catch (error) {
      if (fallbackUrl) window.location.href = fallbackUrl;
    } finally {
      endPendingState(button);
    }
  }

  /**
   * The section ids the cart renders itself from, as a `sections` request parameter.
   * @returns {string}
   */
  function cartItemsSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  /**
   * Dispatches the theme's cart-add event. Mirrors the `CartAddEvent` that
   * product-form-component sends — same name, same detail shape — so every cart
   * listener treats an Élite add like any other add. This file is a plain script
   * rather than a module, so it cannot import that class and builds the event by hand.
   * @param {{ sections?: Record<string, string> }} data - The /cart/add.js response.
   */
  function announceCartAdd(data) {
    const event = new Event('cart:update', { bubbles: true });
    // @ts-expect-error - the theme's cart events carry `detail` on a plain Event.
    event.detail = {
      resource: {},
      sourceId: SOURCE,
      data: { source: SOURCE, itemCount: 1, sections: data?.sections },
    };
    document.dispatchEvent(event);
  }

  /**
   * Clears the add-to-cart pending state. Safe to call twice — the success path
   * clears it early, and `finally` still has to cover the error and reject paths.
   * @param {HTMLButtonElement} button
   */
  function endPendingState(button) {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {boolean} expanded
   */
  function setAllTriggersExpanded(dialog, expanded) {
    const id = dialog.id;
    if (!id) return;
    document.querySelectorAll(`[data-disclosure-open][aria-controls="${id}"]`).forEach((btn) => {
      if (btn instanceof HTMLElement) setExpanded(btn, expanded);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const eliteAtcBtn = target.closest('[data-disclosure-elite-add-to-cart]');
    if (eliteAtcBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      addEliteMembershipToCart(eliteAtcBtn);
      return;
    }

    const openBtn = target.closest('[data-disclosure-open]');
    if (openBtn instanceof HTMLElement) {
      const dialog = getDialog(openBtn);
      if (!(dialog instanceof HTMLDialogElement)) return;

      event.preventDefault();

      const tab = openBtn.getAttribute('data-disclosure-tab') || 'details';
      activateTab(dialog, tab);

      const openedByPointer = event instanceof MouseEvent || event instanceof PointerEvent;
      const onClose = () => {
        setAllTriggersExpanded(dialog, false);
        dialog.removeEventListener('close', onClose);
        dialog.removeAttribute('data-pointer-open');
        if (!openedByPointer) openBtn.focus({ preventScroll: true });
      };
      dialog.addEventListener('close', onClose);

      setAllTriggersExpanded(dialog, true);
      if (openedByPointer) dialog.setAttribute('data-pointer-open', '');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      if (openedByPointer) {
        dialog.setAttribute('tabindex', '-1');
        dialog.focus({ preventScroll: true });
        const clearPointerFlag = () => dialog.removeAttribute('data-pointer-open');
        dialog.addEventListener('keydown', clearPointerFlag, { once: true });
      }
      return;
    }

    const tabBtn = target.closest('[data-disclosure-tab-btn]');
    if (tabBtn instanceof HTMLElement) {
      const dialog = tabBtn.closest('[data-disclosure-dialog]');
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        activateTab(dialog, tabBtn.getAttribute('data-disclosure-tab-btn') || '');
        tabBtn.focus({ preventScroll: true });
      }
      return;
    }

    const closeBtn = target.closest('[data-disclosure-close]');
    if (closeBtn) {
      const dialog = getDialog(closeBtn);
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    const dialog = target.closest('[data-disclosure-dialog]');
    if (dialog instanceof HTMLDialogElement && target === dialog) {
      const rect = dialog.getBoundingClientRect();
      const isBackdropClick =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (isBackdropClick) dialog.close();
    }
  });
})();
